import React, { useState, useEffect, useCallback } from "react";
import ModalWrapper from "../../../../../../../Components/Modals/ModalWrapper";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { MdClose, MdCloudUpload, MdWarning } from "react-icons/md";
import { uploadShipmentPackageImages } from "../../../../../../../services/ShipperServices/Shipments/shipments.services";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../../../../../../store/common/snackBarSlice";

const PackageImageUploadModal = ({ open, onClose, shipment, onSuccess }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();

  const processImageFiles = useCallback((files) => {
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (imageFiles.length === 0) return;

    const newPreviews = [];
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({
          id: Math.random().toString(),
          url: e.target.result,
          file: file,
        });
        if (newPreviews.length === imageFiles.length) {
          setImagePreviews((prev) => [...prev, ...newPreviews]);
          setSelectedImages((prev) => [...prev, ...imageFiles]);
        }
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const handleImageSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      processImageFiles(files);
    }
  };

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const allowedImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/bmp",
        "image/tiff",
      ];

      const imageFiles = [];
      for (let i = 0; i < items.length; i++) {
        if (allowedImageTypes.includes(items[i].type.toLowerCase())) {
          const file = items[i].getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        processImageFiles(imageFiles);
        dispatch(
          showSnackBar({
            text: `${imageFiles.length} image(s) pasted successfully`,
            severity: "success",
          })
        );
      }
    },
    [processImageFiles, dispatch]
  );

  // Add paste event listener when modal is open
  useEffect(() => {
    if (open) {
      document.addEventListener("paste", handlePaste);
      return () => {
        document.removeEventListener("paste", handlePaste);
      };
    }
  }, [open, handlePaste]);

  const handleRemoveImage = (id) => {
    setImagePreviews((prev) => prev.filter((img) => img.id !== id));
    setSelectedImages((prev) => {
      const index = imagePreviews.findIndex((img) => img.id === id);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (selectedImages.length === 0) {
      dispatch(
        showSnackBar({
          text: "Please select at least one image to upload",
          severity: "warning",
        })
      );
      return;
    }

    setUploading(true);
    const formData = new FormData();

    selectedImages.forEach((image) => {
      formData.append("packageImages[]", image);
    });

    try {
      const response = await uploadShipmentPackageImages(
        shipment?.id,
        formData
      );
      if (!response.error) {
        dispatch(
          showSnackBar({
            text:
              response?.data?.message || "Package images uploaded successfully",
            severity: "success",
          })
        );
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        dispatch(
          showSnackBar({
            text:
              response?.error?.response?.data?.message ||
              "Failed to upload images. Please try again.",
            severity: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        showSnackBar({
          text: "An error occurred while uploading images",
          severity: "error",
        })
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    onClose();
  };

  return (
    <ModalWrapper
      isOpen={open}
      onClose={handleClose}
      boxClassName="w-[600px] max-w-[90vw] max-h-[90vh] p-5 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-3 pb-3 border-b border-ui-border flex-shrink-0">
        <h2 className="text-lg font-semibold m-0 text-ui-text">
          Upload Package Images
        </h2>
        <button
          onClick={handleClose}
          className="bg-none border-none cursor-pointer text-xl text-ui-text-muted hover:text-ui-text transition-colors"
        >
          <MdClose />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0 pr-1">
        {/* Warning Message */}
        <div className="bg-state-warning-bg border border-state-warning-border rounded-lg p-3 mb-3 flex items-start gap-2">
          <MdWarning className="text-state-warning-text text-lg flex-shrink-0" />
          <div className="flex-1">
            <strong className="text-state-warning-text block mb-1 text-xs">
              Important: Package Images Required
            </strong>
            <p className="text-state-warning-text text-xs m-0">
              Your shipment cannot be confirmed without package images. Please
              upload clear images of all packages included in this shipment. You
              can also paste images directly (Ctrl+V or Cmd+V).
            </p>
          </div>
        </div>

        {/* Upload Area */}
        <div className="mb-3">
          <label className="flex flex-col items-center justify-center py-5 px-4 border-2 border-dashed border-ui-border rounded-lg cursor-pointer bg-ui-surface-sunken hover:border-brand hover:bg-brand-light transition-all duration-300">
            <MdCloudUpload className="text-4xl text-brand mb-1.5" />
            <span className="text-ui-text text-sm font-medium">
              Click to select images, drag and drop, or paste (Ctrl+V)
            </span>
            <span className="text-ui-text-muted text-xs mt-1">
              Supported formats: JPG, PNG, GIF, WEBP (Max 10MB per image)
            </span>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
          </label>
        </div>

        {/* Image Previews */}
        {imagePreviews.length > 0 && (
          <div className="mb-3">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2">
              {imagePreviews.map((preview) => (
                <div
                  key={preview.id}
                  className="relative w-full pt-[100%] bg-ui-surface-sunken rounded-lg overflow-hidden border border-ui-border"
                >
                  <img
                    src={preview.url}
                    alt="Preview"
                    className="absolute top-0 left-0 w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveImage(preview.id)}
                    className="absolute top-1 right-1 bg-black/60 text-white border-none rounded-full w-5 h-5 cursor-pointer flex items-center justify-center hover:bg-state-danger-solid transition-colors"
                  >
                    <MdClose size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-3 border-t border-ui-border flex-shrink-0">
        <BoltButton
          label="Cancel"
          onClick={handleClose}
          disabled={uploading}
          variant="light"
          size="md"
        />
        <BoltButton
          label={uploading ? "Uploading..." : "Upload Images"}
          onClick={handleUpload}
          disabled={uploading || selectedImages.length === 0}
          loading={uploading}
          variant="primary"
          size="md"
        />
      </div>
    </ModalWrapper>
  );
};

export default PackageImageUploadModal;
